/*
 * lib/supabase.js
 *
 * Lazy Supabase client. Reads VITE_SUPABASE_URL + VITE_SUPABASE_ANON_KEY
 * from environment (set in .env.local). Returns `null` if either is
 * missing so the rest of the app can boot in "hardcoded data" mode
 * without throwing.
 *
 * Migration plan step 5+: once env vars are filled in, the rest of
 * the app starts calling these wrappers instead of mutating state
 * directly. See lib/sync.js (TODO, step 8) for the local-first layer.
 */

import { createClient } from '@supabase/supabase-js';

const url  = import.meta.env.VITE_SUPABASE_URL;
const anon = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const supabase = (url && anon && !url.includes('YOUR-PROJECT'))
  ? createClient(url, anon, {
      auth: { persistSession: true, autoRefreshToken: true }
    })
  : null;

/** Throw a clear error rather than the cryptic "Cannot read of null". */
export function requireSupabase() {
  if (!supabase) {
    throw new Error(
      'Supabase is not configured. Copy .env.example to .env.local and ' +
      'set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.'
    );
  }
  return supabase;
}

// ---------------------------------------------------------------
// Profile + household wrappers — gebruikt door de setup-gate (stap 5)
// en straks door de data-hydrate (stap 6).
// ---------------------------------------------------------------

/** Haal het profile van de ingelogde user op (auto-aangemaakt door trigger). */
export async function fetchProfile(userId) {
  const sb = requireSupabase();
  const { data, error } = await sb
    .from('profiles')
    .select('id, display_name, household_id')
    .eq('id', userId)
    .single();
  if (error) throw error;
  return data;
}

/** Update display_name en/of household_id van de ingelogde user. */
export async function updateProfile(userId, patch) {
  const sb = requireSupabase();
  const { data, error } = await sb
    .from('profiles')
    .update(patch)
    .eq('id', userId)
    .select('id, display_name, household_id')
    .single();
  if (error) throw error;
  return data;
}

/**
 * Maak een nieuw huishouden + koppel het meteen aan je profile in één
 * transactie. We gaan via de RPC-functie `setup_new_household` om twee
 * redenen:
 *   1. RLS-kip-en-ei: na een gewone INSERT mag je je net-ingevoegde rij
 *      nog niet teruglezen, want je profiel verwijst er nog niet naar.
 *   2. Atomisch — de gebruiker eindigt nooit met een huishouden zonder
 *      linking of een profile met een dangling household_id.
 */
export async function setupNewHousehold(displayName, householdName) {
  const sb = requireSupabase();
  const { data, error } = await sb.rpc('setup_new_household', {
    display_name_input: displayName || '',
    household_name: householdName || ''
  });
  if (error) throw error;
  // data = nieuwe households.id (uuid)
  return { id: data, name: householdName };
}

/** Lees een household-rij (alleen toegankelijk als jij er lid van bent). */
export async function fetchHousehold(householdId) {
  const sb = requireSupabase();
  const { data, error } = await sb
    .from('households')
    .select('id, name, has_shared_account')
    .eq('id', householdId)
    .single();
  if (error) throw error;
  return data;
}

/** Update naam van een household waarvan jij lid bent. RLS sluit alle
 *  andere households af, dus we hoeven hier niet zelf te valideren. */
export async function updateHousehold(householdId, patch) {
  const sb = requireSupabase();
  const { data, error } = await sb
    .from('households')
    .update(patch)
    .eq('id', householdId)
    .select('id, name')
    .single();
  if (error) throw error;
  return data;
}

// ---------------------------------------------------------------
// Read-wrappers — stap 6 (hydrate). Geven raw DB-rows terug;
// mapping naar state.S-vorm gebeurt in lib/hydrate.js.
// ---------------------------------------------------------------

/** Volledig profile met alle settings + JSONB-mappen. */
export async function fetchProfileFull(userId) {
  const sb = requireSupabase();
  const { data, error } = await sb
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();
  if (error) throw error;
  return data;
}

/** Alle profielen in het huishouden (inclusief jezelf). Voor partner-lookup. */
export async function fetchHouseholdMembers(householdId) {
  const sb = requireSupabase();
  const { data, error } = await sb
    .from('profiles')
    .select('*')
    .eq('household_id', householdId);
  if (error) throw error;
  return data || [];
}

/** Categorieën van een user. Per-user CRUD-baar — daarom niet globaal. */
export async function fetchCategories(userId) {
  const sb = requireSupabase();
  const { data, error } = await sb
    .from('categories')
    .select('*')
    .eq('user_id', userId)
    .order('sort_order');
  if (error) throw error;
  return data || [];
}

/** Persoonlijke expenses (user_id = userId, household_id null). */
export async function fetchPersonalExpenses(userId) {
  const sb = requireSupabase();
  const { data, error } = await sb
    .from('expenses')
    .select('*')
    .eq('user_id', userId)
    .is('household_id', null)
    .order('sort_order');
  if (error) throw error;
  return data || [];
}

/** Huishoudelijke expenses (household_id = X, user_id null). Voor Samen-view. */
export async function fetchHouseholdExpenses(householdId) {
  const sb = requireSupabase();
  const { data, error } = await sb
    .from('expenses')
    .select('*')
    .eq('household_id', householdId)
    .is('user_id', null)
    .order('sort_order');
  if (error) throw error;
  return data || [];
}

export async function fetchIncomes(userId) {
  const sb = requireSupabase();
  const { data, error } = await sb
    .from('incomes')
    .select('*')
    .eq('user_id', userId)
    .order('sort_order');
  if (error) throw error;
  return data || [];
}

export async function fetchSavingsGoals(userId) {
  const sb = requireSupabase();
  const { data, error } = await sb
    .from('savings_goals')
    .select('*')
    .eq('user_id', userId)
    .order('sort_order');
  if (error) throw error;
  return data || [];
}

/** Persoonlijke extra-entries (hist.ex + hist.in voor één user). */
export async function fetchPersonalExtras(userId) {
  const sb = requireSupabase();
  const { data, error } = await sb
    .from('extra_entries')
    .select('*')
    .eq('user_id', userId)
    .is('household_id', null)
    .order('spent_on');
  if (error) throw error;
  return data || [];
}

/** Huishoudelijke extra-entries (Samen.hist). */
export async function fetchHouseholdExtras(householdId) {
  const sb = requireSupabase();
  const { data, error } = await sb
    .from('extra_entries')
    .select('*')
    .eq('household_id', householdId)
    .is('user_id', null)
    .order('spent_on');
  if (error) throw error;
  return data || [];
}

export async function fetchWeeklyBudgets(userId) {
  const sb = requireSupabase();
  const { data, error } = await sb
    .from('weekly_budgets')
    .select('cat_slug, amount')
    .eq('user_id', userId);
  if (error) throw error;
  return data || [];
}

// ---------------------------------------------------------------
// Write-wrappers — stap 7. UI-mutaties (actions.js) roepen deze
// fire-and-forget aan: lokale state muteert eerst, sync gebeurt
// op de achtergrond. Bij fout enkel console-log (UI-toast = stap 8).
//
// ID-strategie: client genereert UUID via crypto.randomUUID() en
// upsertet die direct, zodat we niet hoeven te wachten op een
// generated-id round-trip.
// ---------------------------------------------------------------

/** Insert of update een expense-rij (vast of var, personal of household). */
export async function upsertExpense(row) {
  const sb = requireSupabase();
  const { data, error } = await sb
    .from('expenses')
    .upsert(row, { onConflict: 'id' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteExpense(id) {
  const sb = requireSupabase();
  const { error } = await sb.from('expenses').delete().eq('id', id);
  if (error) throw error;
}

/** Insert/update een income-rij (alleen personal — incomes is user-scoped). */
export async function upsertIncome(row) {
  const sb = requireSupabase();
  const { data, error } = await sb
    .from('incomes')
    .upsert(row, { onConflict: 'id' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteIncome(id) {
  const sb = requireSupabase();
  const { error } = await sb.from('incomes').delete().eq('id', id);
  if (error) throw error;
}

/** Bulk-delete van alle persoonlijke rijen voor één user. Gebruikt voor
 *  de import-flow: schoonvegen voordat we de backup terug-injecteren.
 *  RLS-policies staan dit toe (user is eigenaar van eigen rijen). */
export async function deleteAllPersonalData(userId) {
  const sb = requireSupabase();
  // Volgorde van tables: extra_entries eerst want die kunnen referenties
  // hebben naar expenses. Categories laatst want vast/var/inkomsten
  // verwijzen naar slugs (geen FK, maar handig om consistent te blijven).
  for (const table of ['extra_entries', 'expenses', 'incomes', 'savings_goals', 'weekly_budgets', 'categories']) {
    const { error } = await sb.from(table).delete().eq('user_id', userId);
    if (error) throw new Error(`Wissen ${table}: ${error.message}`);
  }
}

/** Bulk-delete van alle household-scoped rijen. Gebruikt wanneer de import
 *  óók samen-data bevat — dan moet de samen-state ook verse beginnen. */
export async function deleteAllHouseholdData(householdId) {
  const sb = requireSupabase();
  for (const table of ['extra_entries', 'expenses']) {
    const { error } = await sb.from(table).delete().eq('household_id', householdId);
    if (error) throw new Error(`Wissen ${table} (samen): ${error.message}`);
  }
}

/** Insert/update een categorie-rij voor deze user (PK = user_id + slug). */
export async function upsertCategory(userId, cat) {
  const sb = requireSupabase();
  const row = {
    user_id:    userId,
    slug:       cat.id,
    label:      cat.label || cat.id,
    emoji:      cat.emoji || '📦',
    color:      cat.color || '#888888',
    scope:      cat.scope || 'uit',
    locked:     !!cat.locked,
    sort_order: Number.isFinite(cat.sort_order) ? cat.sort_order : 0
  };
  const { error } = await sb
    .from('categories')
    .upsert(row, { onConflict: 'user_id,slug' });
  if (error) throw error;
}

export async function deleteCategory(userId, slug) {
  const sb = requireSupabase();
  const { error } = await sb
    .from('categories')
    .delete()
    .eq('user_id', userId)
    .eq('slug', slug);
  if (error) throw error;
}

/** Weekbudget per (user, cat_slug). PK is composite — upsert op die combo. */
export async function upsertWeeklyBudget(userId, catSlug, amount) {
  const sb = requireSupabase();
  const { error } = await sb
    .from('weekly_budgets')
    .upsert(
      { user_id: userId, cat_slug: catSlug, amount, updated_at: new Date().toISOString() },
      { onConflict: 'user_id,cat_slug' }
    );
  if (error) throw error;
}

export async function deleteWeeklyBudget(userId, catSlug) {
  const sb = requireSupabase();
  const { error } = await sb
    .from('weekly_budgets')
    .delete()
    .eq('user_id', userId)
    .eq('cat_slug', catSlug);
  if (error) throw error;
}

// ---------------------------------------------------------------
// State.js shape ↔ DB row mapping (forward direction). De inverse
// (DB → state) zit in lib/hydrate.js — die past op de read-flow.
// ---------------------------------------------------------------

/** Map een vast/var state-item naar een expenses-rij voor Supabase.
 *  ctx = { userId, householdId, scope: 'vast'|'var', sortOrder } —
 *  exact één van userId/householdId moet null zijn (CHECK constraint). */
export function expenseStateToRow(item, ctx) {
  const fm = typeof item.freqM === 'number'
    ? item.freqM
    : (item.f === 'j' ? 12 : 1);

  const row = {
    id:           item.__id,
    user_id:      ctx.userId      || null,
    household_id: ctx.householdId || null,
    scope:        ctx.scope,
    name:         item.n,
    amount:       Number(item.a) || 0,
    freq_months:  fm,
    cat_slug:     item.cat,
    sort_order:   ctx.sortOrder || 0
  };
  if (item.c)                   row.color                  = item.c;
  if (item.auto)                row.auto                   = true;
  if (item.domicilie)           row.domicilie              = true;
  if (item.betaaldag)           row.betaaldag              = item.betaaldag;
  if (item.createdAtCycle)      row.created_at_cycle       = item.createdAtCycle;
  if (item.lastPaidCycle)       row.last_paid_cycle        = item.lastPaidCycle;
  if (item.abonnSkipCycle)      row.abonn_skip_cycle       = item.abonnSkipCycle;
  if (item.lastUsedConfirmedAt) row.last_used_confirmed_at = item.lastUsedConfirmedAt;
  // Sinking-fund opt-in. savings_active staat altijd op de rij (boolean
  // met default false) zodat een toggle naar false ook persisteert; via
  // de undefined-check zou false stilletjes wegvallen.
  row.savings_active = !!item.savingsActive;
  if (item.savingsStartedAtCycle) row.savings_started_at_cycle = item.savingsStartedAtCycle;
  return row;
}

/** Map een inkomsten state-item naar een incomes-rij. */
export function incomeStateToRow(item, ctx) {
  const fm = typeof item.freqM === 'number'
    ? item.freqM
    : (item.f === 'j' ? 12 : 1);

  const row = {
    id:          item.__id,
    user_id:     ctx.userId,
    name:        item.n,
    amount:      Number(item.a) || 0,
    freq_months: fm,
    cat_slug:    item.cat,
    sort_order:  ctx.sortOrder || 0
  };
  if (item.startDag) row.start_dag = item.startDag;
  if (item.anker)    row.anker     = true;
  return row;
}

/** Upsert/delete voor savings_goals (= dromen). User-scoped. */
export async function upsertSavingsGoal(row) {
  const sb = requireSupabase();
  const { data, error } = await sb
    .from('savings_goals')
    .upsert(row, { onConflict: 'id' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteSavingsGoal(id) {
  const sb = requireSupabase();
  const { error } = await sb.from('savings_goals').delete().eq('id', id);
  if (error) throw error;
}

/** Map een dromen state-item naar een savings_goals-rij. */
export function savingsGoalStateToRow(item, ctx) {
  return {
    id:           item.__id,
    user_id:      ctx.userId,
    name:         item.n,
    doel:         Number(item.doel) || 0,
    gespaard:     Number(item.gesp) || 0,
    pct:          Number.isFinite(item.pct) ? item.pct : 0,
    color:        item.c || '#5c6bc0',
    sort_order:   ctx.sortOrder || 0,
    // Altijd expliciet meesturen — anders raakt upsert deze kolommen niet
    // aan en blijft de oude DB-waarde behouden (bvb. weggehaalde deadline
    // die terugkomt na refresh).
    is_noodfonds: !!item.isNoodfonds,
    paused:       !!item.paused,
    deadline:     item.deadline || null
  };
}

/** Upsert/delete voor extra_entries (= hist.ex / hist.in entries). */
export async function upsertExtraEntry(row) {
  const sb = requireSupabase();
  const { data, error } = await sb
    .from('extra_entries')
    .upsert(row, { onConflict: 'id' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteExtraEntry(id) {
  const sb = requireSupabase();
  const { error } = await sb.from('extra_entries').delete().eq('id', id);
  if (error) throw error;
}

/** Map een hist-entry naar een extra_entries-rij.
 *  ctx = { userId, householdId, direction:'uit'|'in', cycleKey, relatedExpenseId }. */
export function extraEntryStateToRow(item, ctx) {
  return {
    id:                 item.__id,
    user_id:            ctx.userId      || null,
    household_id:       ctx.householdId || null,
    direction:          ctx.direction,
    amount:             Number(item.a) || 0,
    cat_slug:           item.cat,
    description:        item.desc || null,
    spent_on:           parseHistDate(item.date) || new Date().toISOString().slice(0, 10),
    cycle_key:          ctx.cycleKey,
    // Altijd expliciet meesturen (ook null) — anders raakt upsert deze
    // kolommen niet aan en houdt de DB een verouderde link/begroot vast.
    related_expense_id: ctx.relatedExpenseId || null,
    begroot:            !!item.begroot
  };
}

/** D/M/YYYY → YYYY-MM-DD (DB expects ISO). null als parse mislukt. */
function parseHistDate(str) {
  if (!str) return null;
  const m = String(str).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const [, d, mo, y] = m;
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

// ---------------------------------------------------------------
// Profile patch — state.S[user] fields zoals settings, toggles,
// JSONB-mappen (gem_handled/spaar_handled/cycle_reviews/...).
// ---------------------------------------------------------------

/** Update een gedeeltelijke profile-patch (= elke combinatie van DB-kolommen). */
export async function patchProfile(userId, dbPatch) {
  const sb = requireSupabase();
  const { error } = await sb
    .from('profiles')
    .update(dbPatch)
    .eq('id', userId);
  if (error) throw error;
}

/** Converteer een state-style patch (camelCase, JS-keys) naar de DB-kolommen. */
export function profilePatchStateToDb(patch) {
  const out = {};
  if ('shareWithMate'      in patch) out.share_with_mate      = !!patch.shareWithMate;
  if ('gemAuto'            in patch) out.gem_auto             = !!patch.gemAuto;
  if ('spaarAuto'          in patch) out.spaar_auto           = !!patch.spaarAuto;
  if ('gemHandled'         in patch) out.gem_handled          = patch.gemHandled         ?? {};
  if ('spaarHandled'       in patch) out.spaar_handled        = patch.spaarHandled       ?? {};
  if ('loonHandled'        in patch) out.loon_handled         = patch.loonHandled        ?? {};
  if ('surplusHandled'     in patch) out.surplus_handled      = patch.surplusHandled     ?? {};
  if ('cycleReviews'       in patch) out.cycle_reviews        = patch.cycleReviews       ?? {};
  if ('huidigSaldo'        in patch) out.huidig_saldo         = patch.huidigSaldo;
  if ('huidigSaldoUpdated' in patch) out.huidig_saldo_updated = patch.huidigSaldoUpdated;
  if ('pensioenActief'     in patch) out.pensioen_actief      = !!patch.pensioenActief;
  if ('pensioenGrens'      in patch) out.pensioen_grens       = Number(patch.pensioenGrens);
  if ('belastingPct'       in patch) out.belasting_pct        = Number(patch.belastingPct);
  if ('schulden'           in patch) out.schulden             = patch.schulden           ?? [];
  if ('externeSchulden'    in patch) out.externe_schulden     = patch.externeSchulden    ?? [];
  // null = geen actieve noodfonds-burst; object = burst loopt (key=droomnaam, val=pre-burst pct).
  if ('noodfondsPctSnapshot' in patch) out.noodfonds_pct_snapshot = patch.noodfondsPctSnapshot ?? null;
  if ('appStartDate'       in patch) out.app_start_date       = patch.appStartDate || null;
  if ('mode'               in patch) out.theme_mode           = patch.mode;
  if ('theme'              in patch) out.theme_palette        = patch.theme;
  if ('onboardingDone'     in patch) out.onboarding_done      = !!patch.onboardingDone;
  if ('remainingView'      in patch) out.remaining_view       = patch.remainingView;
  return out;
}
